import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { animate, motion, useInView } from "framer-motion";
import { destinations } from "@/data/destinations";
import { trustStats } from "@/data/content";
import { Reveal } from "@/components/site/motion";
import { SectionHeading } from "@/components/site/Blocks";

const CountUp = ({ value, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const target = parseInt(value.replace(/[^\d]/g, ""), 10) || 0;
  const tail = value.replace(/^[\d,]+/, "");
  const fmt = (n) => Math.round(n).toLocaleString("en-US");
  const [shown, setShown] = useState(fmt(target));
  useEffect(() => {
    if (!inView) return undefined;
    setShown("0");
    const c = animate(0, target, { duration: 2.4, delay, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setShown(fmt(v)) });
    return () => c.stop();
  }, [inView, target, delay]);
  const suffix = tail === "+" ? <sup className="ml-1 align-super font-sans text-[0.45em] font-light text-gold">+</sup> : tail;
  return (
    <span ref={ref} className="inline-grid">
      <span className="invisible [grid-area:1/1]" aria-hidden="true">{fmt(target)}{suffix}</span>
      <span className="[grid-area:1/1]" aria-label={value}>{shown}{suffix}</span>
    </span>
  );
};

export const TrustBar = () => (
  <section className="bg-navy text-ivory" data-testid="trust-bar">
    <div className="container-lux grid grid-cols-2 gap-x-10 gap-y-16 py-20 md:py-28 lg:grid-cols-4">
      {trustStats.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.1}>
          <p className="font-['Bodoni_Moda',serif] text-5xl font-normal tracking-[-0.01em] text-gold [font-variant-numeric:lining-nums_proportional-nums] md:text-7xl lg:text-[5.5rem] lg:leading-none" data-testid={`trust-stat-${i}`}><CountUp value={s.value} delay={i * 0.12} /></p>
          <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.3em] text-ivory/75">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const spans = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7", "lg:col-span-6", "lg:col-span-6"];

const DestCard = ({ d, i }) => (
  <Reveal delay={(i % 2) * 0.12} className={`${spans[i]} ${i % 2 ? "lg:mt-32" : ""}`}>
    <Link to={`/destinations/${d.slug}`} data-testid={`destination-card-${d.slug}`} className="group block">
      <div className="relative h-[420px] overflow-hidden bg-navy md:h-[560px]">
        <img src={d.image} alt={`${d.city}, ${d.name}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.8s] ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-[1.05]" />
        <div className="absolute inset-0 bg-navy/10 transition-colors duration-700 group-hover:bg-navy/0" />
      </div>
      <div className="mt-8 flex items-start justify-between gap-6">
        <div>
          <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.3em] text-gold-deep">0{i + 1} — {d.city}</p>
          <h3 className="mt-4 font-serif text-5xl font-light text-navy md:text-6xl">{d.name}</h3>
          <p className="mt-4 max-w-md text-[16px] font-light leading-relaxed text-charcoal-soft">{d.tagline}</p>
        </div>
        <ArrowUpRight className="mt-2 h-7 w-7 shrink-0 text-navy/40 transition-all duration-700 group-hover:rotate-45 group-hover:text-gold-deep" strokeWidth={1.25} />
      </div>
    </Link>
  </Reveal>
);

export const Destinations = () => (
  <section className="bg-ivory section-lux" data-testid="home-destinations">
    <div className="container-lux">
      <div className="flex flex-col justify-between gap-10 md:flex-row md:items-end">
        <SectionHeading eyebrow="Destinations" lines={["Six gateways.", <em key="e" className="italic text-gold-deep">One trusted adviser.</em>]} />
        <Reveal><p className="lede max-w-sm text-charcoal-soft">Considered pathways to the world's most desirable places to live, work and invest — each one tailored, subject to eligibility.</p></Reveal>
      </div>
      <motion.div className="mt-24 grid gap-x-12 gap-y-24 md:mt-32 lg:grid-cols-12 lg:gap-y-16">
        {destinations.map((d, i) => <DestCard key={d.slug} d={d} i={i} />)}
      </motion.div>
    </div>
  </section>
);
